import { FC } from "react";
import toast from "react-hot-toast";

import { ACCOUNT_DISPOSITION_STATUSES } from "shared/lib/constants/account";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { AccountDetailsStatusI } from "shared/lib/interfaces/account";

import { useFetchTopTen, useRemoveFromTopTen } from "../data/queries";
import { isOnTopTen } from "../lib/data";
import { describeTopTenFailure } from "../lib/errors";
import {
  ADD_TO_TOP_TEN_MODAL_ID,
  AddToTopTenModal,
} from "./add-to-top-ten-modal";

/** Statuses a lead can be added from: adding makes it a nurture. */
const ADDABLE_STATUSES: Array<string> = [
  ACCOUNT_DISPOSITION_STATUSES.NURTURE,
  ACCOUNT_DISPOSITION_STATUSES.COLD_LEAD,
];

interface AccountTopTenActionPropsI {
  campaignId?: string;
  accountId?: string;
  accountStatus?: AccountDetailsStatusI;
  onAccountUpdate?: () => void;
}

/**
 * "Add to Top 10" / "Remove from Top 10" on the lead's page (C-16 S4). Shows
 * nothing while the backend switch is off, while it loads, or when the read
 * fails (fail closed).
 */
export const AccountTopTenAction: FC<AccountTopTenActionPropsI> = ({
  campaignId,
  accountId,
  accountStatus,
  onAccountUpdate,
}) => {
  const { data } = useFetchTopTen();
  const { mutateAsync: removeFromTopTen, isPending: isRemoving } =
    useRemoveFromTopTen();

  if (!data?.isSwitchedOn || !campaignId || !accountId) {
    return null;
  }

  if (isOnTopTen(data, campaignId, accountId)) {
    const handleRemove = async () => {
      try {
        await removeFromTopTen({ campaignId, accountId });
      } catch (error) {
        toast.error(
          describeTopTenFailure(error, "Failed to remove from your Top 10")
        );

        return;
      }

      toast.success("Removed from your Top 10");
      onAccountUpdate?.();
    };

    return (
      <button
        type="button"
        className="ae-link brand-typography-body6"
        disabled={isRemoving}
        onClick={handleRemove}
      >
        Remove from Top 10
      </button>
    );
  }

  if (!ADDABLE_STATUSES.includes(accountStatus?.status as string)) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="ae-link brand-typography-body6"
        onClick={() => modalHelpers.open(ADD_TO_TOP_TEN_MODAL_ID)}
      >
        Add to Top 10
      </button>

      <AddToTopTenModal
        campaignId={campaignId}
        accountId={accountId}
        isFull={data.items.length >= data.limit}
        limit={data.limit}
        onSuccess={onAccountUpdate}
      />
    </>
  );
};
